import { Node } from 'reactflow';
import { MAX_X_ALLOWED_FOR_NODE_PLACEMENT } from '@constants/canvas/general';
import NodeView from '@utils/canvas/NodeView';

export interface Delta {
  dx: number;
  dy: number;
}

interface XYBounds {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
}

/**
 * Band-relative movement bounds for a single entity node.
 * X: [0, MAX_X_ALLOWED_FOR_NODE_PLACEMENT] (canvas placement width).
 * Y: from row 0 down to the last occupied row of the node's own sub-layer
 * band, so a node can never leave its band (AC3). The row range is derived
 * purely from sibling node Y positions — no DOM measurement.
 */
export const getNodeBounds = (node: Node, allNodes: Node[]): XYBounds => {
  const bandNodes = allNodes.filter(
    (candidate) => candidate.parentNode === node.parentNode,
  );

  const lastRowIndex = bandNodes.reduce(
    (max, bandNode) =>
      Math.max(max, NodeView.calculateRowByY(bandNode.position.y)),
    0,
  );

  return {
    minX: 0,
    maxX: MAX_X_ALLOWED_FOR_NODE_PLACEMENT,
    minY: NodeView.calculateYPosition(0),
    maxY: NodeView.calculateYPosition(lastRowIndex),
  };
};

/**
 * Clamps a raw (dx, dy) offset so that NO group member leaves its own bounds.
 * The group is rigid: the most-constrained member limits travel in each
 * direction (AC4). `startPositions` holds each member's position captured at
 * drag start.
 */
export const clampGroupDelta = (
  groupNodes: Node[],
  startPositions: Map<string, { x: number; y: number }>,
  allNodes: Node[],
  raw: Delta,
): Delta => {
  let minDx = -Infinity;
  let maxDx = Infinity;
  let minDy = -Infinity;
  let maxDy = Infinity;

  groupNodes.forEach((node) => {
    const start = startPositions.get(node.id);
    if (!start) return;

    const bounds = getNodeBounds(node, allNodes);

    minDx = Math.max(minDx, bounds.minX - start.x);
    maxDx = Math.min(maxDx, bounds.maxX - start.x);
    minDy = Math.max(minDy, bounds.minY - start.y);
    maxDy = Math.min(maxDy, bounds.maxY - start.y);
  });

  return {
    dx: Math.min(Math.max(raw.dx, minDx), maxDx),
    dy: Math.min(Math.max(raw.dy, minDy), maxDy),
  };
};

/**
 * The currently-visible members of the active multi-selection (AC11):
 * selected, present in the store, and not hidden by a filter/search.
 */
export const getVisibleSelectedNodes = (
  selectedIds: string[],
  allNodes: Node[],
): Node[] => {
  const selected = new Set(selectedIds);

  return allNodes.filter((node) => selected.has(node.id) && !node.hidden);
};
